import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, RichTextContent, useContentToken, useTokens } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getAccessibleTextOnBackground, parseColor } from '../utils/luminance';

/** True for CSS colors that don't represent a painted surface. */
function isTransparentColor(color: string | undefined): boolean {
  if (!color) return true;
  const s = color.trim().toLowerCase();
  if (!s || s === 'transparent') return true;
  return /^rgba?\(\s*0(?:\.0+)?\s*[, ]\s*0(?:\.0+)?\s*[, ]\s*0(?:\.0+)?(?:\s*[,/]\s*0(?:\.0+)?)?\s*\)$/.test(s);
}

/**
 * Prefer the section's painted background, then the live `--vibe-color-surface`
 * CSS variable (property overrides live here), then the JS token fallback.
 */
function resolveSurfaceBackground(el: HTMLElement | null, tokenSurface?: string): string | undefined {
  if (el && typeof getComputedStyle !== 'undefined') {
    const styles = getComputedStyle(el);
    const painted = styles.backgroundColor;
    if (!isTransparentColor(painted) && parseColor(painted)) return painted;
    const surfaceVar = styles.getPropertyValue('--vibe-color-surface').trim();
    if (surfaceVar) return surfaceVar;
  }
  return tokenSurface;
}

// ============================================================================
// Icons — plain SVGs colored via `currentColor` (white on the dark badge)
// ============================================================================

function IconWaivedFees() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14" aria-hidden="true">
      <circle cx="24" cy="24" r="18" />
      <path d="M18 18l12 12" />
      <path d="M28 18h-8v6" />
      <path d="M20 30h8v-6" />
    </svg>
  );
}

function IconSavings() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14" aria-hidden="true">
      <circle cx="24" cy="24" r="16" />
      <path d="M24 14v20" />
      <path d="M28 18h-6a3 3 0 000 6h4a3 3 0 010 6h-6" />
    </svg>
  );
}

function IconFastTrack() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14" aria-hidden="true">
      <path d="M8 24h24" />
      <path d="M26 16l8 8-8 8" />
      <path d="M36 12v24" />
      <path d="M40 8v32" />
    </svg>
  );
}

function IconWelcome() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14" aria-hidden="true">
      <path d="M8 22l16-12 16 12" />
      <path d="M12 20v18h24V20" />
      <path d="M20 38v-10h8v10" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * Preferred Employer benefits grid: a 4-card row that summarises the marquee
 * perks members receive. Icon + short title + one-line description per card,
 * over the site's warm surface color. Structurally mirrors the FRC benefits
 * grid inherited from blue-haven, but pared down to four columns/rows so it
 * balances against the intro copy above.
 */
export function PreferredEmployerBenefits() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();

  const heading = useContentToken('preferredemployerbenefits.heading');
  const subheading = useContentToken('preferredemployerbenefits.subheading');

  const b1Title = useContentToken('preferredemployerbenefits.benefit1_title');
  const b1Desc = useContentToken('preferredemployerbenefits.benefit1_desc');
  const b2Title = useContentToken('preferredemployerbenefits.benefit2_title');
  const b2Desc = useContentToken('preferredemployerbenefits.benefit2_desc');
  const b3Title = useContentToken('preferredemployerbenefits.benefit3_title');
  const b3Desc = useContentToken('preferredemployerbenefits.benefit3_desc');
  const b4Title = useContentToken('preferredemployerbenefits.benefit4_title');
  const b4Desc = useContentToken('preferredemployerbenefits.benefit4_desc');

  // Copy sits on `--vibe-color-surface`, which properties freely override
  // (often a dark `property_color_3`). Themed `--vibe-color-text` is paired
  // with the page background, so JS tokens alone can pick dark-on-dark.
  // Read the painted surface, keep brand colors when they clear WCAG AA,
  // otherwise fall back to a luminance-picked black/white.
  const sectionRef = useRef<HTMLElement>(null);
  const tokens = useTokens();
  const [surfaceBg, setSurfaceBg] = useState<string | undefined>(tokens?.color?.surface);

  useLayoutEffect(() => {
    setSurfaceBg(resolveSurfaceBackground(sectionRef.current, tokens?.color?.surface));
  }, [tokens]);

  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(surfaceBg, tokens?.color?.primary),
    [surfaceBg, tokens],
  );
  const bodyColor = useMemo(
    () => getAccessibleTextOnBackground(surfaceBg, tokens?.color?.text),
    [surfaceBg, tokens],
  );
  const copyStyle = useMemo<CSSProperties>(
    () =>
      ({
        ['--peb-heading' as string]: headingColor,
        ['--peb-body-text' as string]: bodyColor,
        color: bodyColor,
      }) as CSSProperties,
    [headingColor, bodyColor],
  );

  return (
    <section
      ref={sectionRef}
      className="peb-section w-full bg-[var(--vibe-color-surface)]"
      style={copyStyle}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Heading */}
        <div ref={headingRef} className="reveal-fade-up text-center mb-10 md:mb-14">
          <RichTextContent
            path="preferredemployerbenefits.heading"
            label="Heading"
            defaultValue="<p>Exclusive Member Benefits</p>"
            as="h2"
            className="peb-heading font-heading text-3xl sm:text-4xl md:text-5xl font-semibold uppercase tracking-wide leading-[1.1] mb-4"
            style={{ color: headingColor }}
          />
          <EditableToken
            path="preferredemployerbenefits.subheading"
            as="p"
            className="peb-subheading font-body text-base leading-relaxed max-w-3xl mx-auto"
          >
            {subheading}
          </EditableToken>
          {/* Anchor the heading token even when RichTextContent handles rendering. */}
          <EditableToken
            path="preferredemployerbenefits.heading"
            as="span"
            className="hidden"
            style={{ display: 'none' }}
          >
            {heading || ''}
          </EditableToken>
        </div>

        {/* Benefit cards */}
        <div
          ref={gridRef}
          className="reveal-fade-up grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10"
        >
          {/* Waived Application Fees */}
          <div className="peb-card">
            <div className="peb-card-icon">
              <IconWaivedFees />
            </div>
            <EditableToken
              path="preferredemployerbenefits.benefit1_title"
              as="h3"
              className="peb-card-title"
            >
              {b1Title}
            </EditableToken>
            <EditableToken
              path="preferredemployerbenefits.benefit1_desc"
              as="p"
              className="peb-card-desc"
            >
              {b1Desc}
            </EditableToken>
          </div>

          {/* Reduced Deposit */}
          <div className="peb-card">
            <div className="peb-card-icon">
              <IconSavings />
            </div>
            <EditableToken
              path="preferredemployerbenefits.benefit2_title"
              as="h3"
              className="peb-card-title"
            >
              {b2Title}
            </EditableToken>
            <EditableToken
              path="preferredemployerbenefits.benefit2_desc"
              as="p"
              className="peb-card-desc"
            >
              {b2Desc}
            </EditableToken>
          </div>

          {/* Priority Leasing */}
          <div className="peb-card">
            <div className="peb-card-icon">
              <IconFastTrack />
            </div>
            <EditableToken
              path="preferredemployerbenefits.benefit3_title"
              as="h3"
              className="peb-card-title"
            >
              {b3Title}
            </EditableToken>
            <EditableToken
              path="preferredemployerbenefits.benefit3_desc"
              as="p"
              className="peb-card-desc"
            >
              {b3Desc}
            </EditableToken>
          </div>

          {/* Welcome Package */}
          <div className="peb-card">
            <div className="peb-card-icon">
              <IconWelcome />
            </div>
            <EditableToken
              path="preferredemployerbenefits.benefit4_title"
              as="h3"
              className="peb-card-title"
            >
              {b4Title}
            </EditableToken>
            <EditableToken
              path="preferredemployerbenefits.benefit4_desc"
              as="p"
              className="peb-card-desc"
            >
              {b4Desc}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .peb-card {
          text-align: center;
          padding: 1rem 0.5rem;
        }
        .peb-card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 6.5rem;
          height: 6.5rem;
          margin: 0 auto 1.25rem;
          border-radius: 50%;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .peb-card-icon svg {
          width: 3.5rem;
          height: 3.5rem;
        }
        .peb-heading,
        .peb-heading * {
          color: var(--peb-heading, var(--vibe-color-text));
        }
        .peb-subheading {
          color: var(--peb-body-text, var(--vibe-color-text));
        }
        .peb-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.1rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--peb-heading, var(--peb-body-text, var(--vibe-color-text)));
          margin: 0 0 0.6rem;
        }
        .peb-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--peb-body-text, var(--vibe-color-text));
          line-height: 1.6;
          margin: 0;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerBenefits',
  displayName: 'Preferred Employer Benefits',
  tokens: {
    'preferredemployerbenefits.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Exclusive Member Benefits</p>',
    },
    'preferredemployerbenefits.subheading': {
      label: 'Subheading',
      type: 'text',
      defaultValue:
        'Qualifying employees enjoy meaningful savings and a red-carpet leasing experience the moment they walk through our doors.',
    },
    'preferredemployerbenefits.benefit1_title': {
      label: 'Benefit 1 Title',
      type: 'text',
      defaultValue: 'Waived Application Fees',
    },
    'preferredemployerbenefits.benefit1_desc': {
      label: 'Benefit 1 Description',
      type: 'text',
      defaultValue:
        'Skip the application fee at move-in — a simple thank-you for choosing to lease with us.',
    },
    'preferredemployerbenefits.benefit2_title': {
      label: 'Benefit 2 Title',
      type: 'text',
      defaultValue: 'Reduced Security Deposit',
    },
    'preferredemployerbenefits.benefit2_desc': {
      label: 'Benefit 2 Description',
      type: 'text',
      defaultValue:
        'Qualified members receive a reduced security deposit, so moving in is easier on your budget.',
    },
    'preferredemployerbenefits.benefit3_title': {
      label: 'Benefit 3 Title',
      type: 'text',
      defaultValue: 'Priority Leasing',
    },
    'preferredemployerbenefits.benefit3_desc': {
      label: 'Benefit 3 Description',
      type: 'text',
      defaultValue: 'Fast-track tours and application review, so your next home is ready when you are.',
    },
    'preferredemployerbenefits.benefit4_title': {
      label: 'Benefit 4 Title',
      type: 'text',
      defaultValue: 'A Warm Welcome',
    },
    'preferredemployerbenefits.benefit4_desc': {
      label: 'Benefit 4 Description',
      type: 'text',
      defaultValue:
        'A move-in welcome package and a dedicated point of contact to make your first days feel like home.',
    },
  },
};

(PreferredEmployerBenefits as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerBenefits;
